/**
 * Public Rails UI endpoint for newsletter signup (no API key).
 * @see POST /ui/subscribe/:platform_id/:newsletter_slug
 */
export type UiNewsletterSubscribeResult =
  | { ok: true }
  | {
      ok: false;
      reason: "not_found" | "client_error" | "server_error" | "network" | "missing_config";
    };

export type UiNewsletterSubscribeBody = {
  email: string;
  /** Optional subscriber name */
  name?: string;
  recaptcha_token: string;
};

function apiOrigin(): string | null {
  const base = process.env.NEXT_PUBLIC_ITIN_API_URL?.replace(/\/$/, "");
  return base || null;
}

/**
 * Subscribe via the marketing-site UI route. Prefer JSON (empty 200 on success).
 */
export async function postUiNewsletterSubscribe(
  platformId: number | string,
  newsletterSlug: string,
  body: UiNewsletterSubscribeBody,
): Promise<UiNewsletterSubscribeResult> {
  const origin = apiOrigin();
  if (!origin) {
    return { ok: false, reason: "missing_config" };
  }

  const url = `${origin}/ui/subscribe/${encodeURIComponent(String(platformId))}/${encodeURIComponent(newsletterSlug)}`;

  const params = new URLSearchParams();
  params.set("email", body.email.trim());
  if (body.name?.trim()) {
    params.set("name", body.name.trim());
  }
  params.set("recaptcha_token", body.recaptcha_token);

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (res.ok) {
      return { ok: true };
    }
    if (res.status === 404) {
      return { ok: false, reason: "not_found" };
    }
    if (res.status >= 400 && res.status < 500) {
      return { ok: false, reason: "client_error" };
    }
    return { ok: false, reason: "server_error" };
  } catch {
    return { ok: false, reason: "network" };
  }
}
